import React, { Component } from "react";
import { format } from "date-fns";
import CircularProgress from "@material-ui/core/CircularProgress";
import axios from "axios";
import AxiosClient from "../../../Network/AxiosClient";
import { FacematchAxiosClient } from "../../../Network/FacematchAxiosClient";
import { filter_whitelist_assisted_ekyc } from "../Configs/filterConfigs";
import { NotificationContext } from "../../Notifications/NotificationProvider";
import { v4 } from "uuid";

const AUTO_DOWNLOAD_TIMEOUT_MS = 30 * 60 * 1000;

export default class DownloadReport extends Component {
  static contextType = NotificationContext;

  constructor(props) {
    super(props);
    this.state = {
      fetchingFileName: null,
      status: {},
      reportReady: false,
      downloadUrl: null,
      generationTime: null,
      autoDownloadTimer: null,
    };
  }

  _initiateDownload = (report) => {
    if (this.state.fetchingFileName) return;
    if (report.isGeneratable) {
      this.generateReportFromRequest(report);
    } else if (report.indirectDownload) {
      this.downloadFromUrl(report);
    } else {
      this.download(report);
    }
  };

  _alternateDownload = (report) => {
    this.setState({ fetchingFileName: report.key });
    if (report.indirectDownload) {
      this.downloadFromUrl(report);
    } else {
      this.generateReportFromRequest(report);
    }
  };

  componentWillUnmount() {
    if (this.state.autoDownloadTimer) {
      clearTimeout(this.state.autoDownloadTimer);
    }
  }

  startAutoDownloadTimer = () => {
    const timer = setTimeout(() => {
      if (this.state.reportReady && this.state.downloadUrl) {
        this.performDelayedDownload();
      }
    }, AUTO_DOWNLOAD_TIMEOUT_MS);

    this.setState({ autoDownloadTimer: timer });
  };

  performDelayedDownload = () => {
    if (this.state.downloadUrl) {
      this.downloadGeneratableReport(this.props.report, this.state.downloadUrl);
      this.setState({
        reportReady: false,
        downloadUrl: null,
        generationTime: null,
        autoDownloadTimer: null,
      });
    }
  };

  resetButtonState = () => {
    if (this.state.autoDownloadTimer) {
      clearTimeout(this.state.autoDownloadTimer);
    }
    this.setState({
      reportReady: false,
      downloadUrl: null,
      generationTime: null,
      autoDownloadTimer: null,
      fetchingFileName: null,
      status: {},
      dumpReportInitialized: false,
    });
  };

  showNotification = (type, message) => {
    const dispatch = this.context;
    if (dispatch) {
      dispatch({
        type: "ADD_NOTIFICATION",
        payload: {
          id: v4(),
          type,
          message,
        },
      });
    }
  };

  componentDidUpdate(prevProps, prevState) {
    const item = prevProps.report;
    const statusObj = this.state.status;
    const prevStatusObj = prevState.status;
    const isMerchantPerformanceReport = item?.key === "trn-report";

    if (prevProps.form !== this.props.form) {
      this.resetButtonState();
      return;
    }

    if (
      (prevStatusObj.status !== statusObj.status &&
        statusObj.status === "Error") ||
      (prevStatusObj.status === "Pending" && statusObj.status === "Not Found")
    ) {
      let errorMessage = "No data found";
      if (item.fields?.fields?.date) {
        errorMessage = `${errorMessage} for the selected date`;
      }
      this.setState({ fetchingFileName: null });
      alert(errorMessage);
    } else if (statusObj.status === "Pending")
      setTimeout(() => {
        this.getReportStatus(item);
      }, 30000);
    else if (
      prevStatusObj.status !== statusObj.status &&
      statusObj.status === "Generated" &&
      statusObj.error &&
      statusObj.error.length > 0
    ) {
      this.setState({ fetchingFileName: null });
      alert(statusObj.error);
    } else if (
      prevStatusObj.status !== statusObj.status &&
      statusObj.status === "Generated"
    ) {
      if (isMerchantPerformanceReport) {
        this.setState({
          reportReady: true,
          downloadUrl: statusObj.downloadUrl,
          generationTime: new Date(),
          fetchingFileName: null,
        });
        this.startAutoDownloadTimer();
      } else {
        this.downloadGeneratableReport(item, statusObj.downloadUrl);
      }
    }
  }

  getReportStatus = async (report) => {
    const body = this.generatePostBody(report);
    return AxiosClient.get(
      `${process.env.REACT_APP_API_REPORT}/api/reports/v3/get-report-generation-status`,
      {
        params: {
          status_check_report_name: report.statusKey,
          ...body,
        },
        headers: {
          Authorization:
            "Bearer " + JSON.parse(localStorage.getItem("currUserToken")),
        },
      }
    )
      .then((res) => {
        this.setState({ status: { ...res.data } });
        return { ...res.data };
      })
      .catch((err) => {
        console.log("Error from get-report-generation-status ", err);
      });
  };

  generateReportFromRequest = async (report) => {
    if (report.key === "dump-report") {
      await this.initializeMerchantLoanDumpReport(report);
    }

    const statusObj = await this.getReportStatus(report);
    const requestBody = this.generatePostBody(report);
    if (report.minIOParam) Object.assign(requestBody, { is_minio: true });

    const isMerchantPerformanceReport = report.key === "trn-report";

    if (statusObj.status === "Downloaded") {
      if (isMerchantPerformanceReport) {
        this.setState({
          reportReady: true,
          downloadUrl: statusObj.downloadUrl,
          generationTime: new Date(),
          fetchingFileName: null,
        });
        this.startAutoDownloadTimer();
      } else {
        this.downloadGeneratableReport(report, statusObj.downloadUrl);
      }
      return;
    }

    if (statusObj.status === "Not Found") {
      AxiosClient.post(
        `${process.env.REACT_APP_API_REPORT}${report.url}`,
        { ...requestBody },
        {
          headers: {
            Authorization:
              "Bearer " + JSON.parse(localStorage.getItem("currUserToken")),
          },
        }
      )
        .then((res) => {
          this.getReportStatus(report);
        })
        .catch((err) => {});
    }

    if (statusObj.status === "Error") {
      let errorMessage = "No data found";
      if (requestBody.date) {
        errorMessage = `${errorMessage} for the selected date.`;
      }
      if (
        (requestBody.start_date && requestBody.end_date) ||
        (requestBody.start_time && requestBody.end_time)
      ) {
        errorMessage = `${errorMessage} for the selected date range.`;
      }
      this.showNotification("ERROR", errorMessage);
      this.setState({ fetchingFileName: null });
    }
  };

  initializeMerchantLoanDumpReport = async (report) => {
    const requestBody = this.generatePostBody(report);
    try {
      await AxiosClient.post(
        `${process.env.REACT_APP_API_REPORT}${report.url}`,
        { ...requestBody },
        {
          headers: {
            Authorization:
              "Bearer " + JSON.parse(localStorage.getItem("currUserToken")),
          },
        }
      );
    } catch (error) {
      console.error(
        "Error in merchant loan dump report initialization:",
        error
      );
      this.showNotification(
        "ERROR",
        "Failed to initialize dump report generation."
      );
      this.setState({ fetchingFileName: null });

      throw error;
    }
  };

  generatePostBody = (report) => {
    const form = { ...this.props.form };
    const fields = report.fields.fields;
    var postBody = {};
    var availableKeys =
      report.key === "assisted-ekyc-report"
        ? filter_whitelist_assisted_ekyc[form["filter_type"].value].filter(
            (f) => f !== "filter_type"
          )
        : Object.keys(fields);
    Object.keys(fields)
      .filter((key) => form.hasOwnProperty(key))
      .filter((key) => availableKeys.includes(key))
      .forEach((key) => {
        //console.log(key, form[key].type, form[key].value);
        let fieldValue;

        if (key === "request_types") {
          // even though this is not a multi select, we are treating it as an array to accomodate api expectations
          fieldValue = form[key].value === "" ? null : [form[key].value];
        } else if (key === "l1_action_types" || key === "l2_action_types") {
          fieldValue =
            Array.isArray(form[key].value) && form[key].value.length === 0
              ? null
              : form[key].value;
        } else {
          fieldValue =
            form[key].type &&
            form[key].type === "date-picker" &&
            form[key].value !== ""
              ? format(new Date(form[key].value), "yyyy-MM-dd")
              : form[key].value;
        }

        postBody = {
          ...postBody,
          [key]: fieldValue,
        };
      });

    return postBody;
  };
  generateFileName = (report, body) => {
    const form = { ...this.props.form };

    var linkName = "";
    if (report.fileName) {
      report.fileName.forEach((i) => {
        var temp = "";
        if (i.source === "string") {
          temp = i.value;
        }
        if (i.source === "form") {
          var options = form[i.value]?.options;
          temp = options.find((e) => e.id === body[i.value])?.name;
        }
        if (i.source === "postbody") {
          temp = body ? body[i.value] : "";
        }
        temp = temp + "-";
        linkName = linkName + temp;
      });
    } else linkName = `${report.key}`;

    linkName =
      linkName +
      `_downloaded at ${format(new Date(), "yyyy-MM-dd")}.${report.file_type}`;

    return linkName;
  };

  download = (report) => {
    const body = this.generatePostBody(report);

    if (body === null) {
      return;
    }

    this.setState({ fetchingFileName: report.key });

    if (report.key === "download-selfie-report") {
      const { start_time: startDate, end_time: endDate, ...rest } = body;
      FacematchAxiosClient.get(`report/download-selfie-report`, {
        params: { startDate, endDate, ...rest, appName: "nimontron" },
        responseType: "blob",
      })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `${report.key}_${format(new Date(), "yyyy-MM-dd")}.${
              report.file_type
            }`
          );
          document.body.appendChild(link);
          link.click();
        })
        .then(() => this.setState({ fetchingFileName: null }))
        .catch((err) => {
          console.error("Selfie report download failed with error:", err);
          console.log("Selfie report download failed with error:", err);
          this.setState({ fetchingFileName: null });
          alert(
            "Something went wrong ! Please contact your administrator for details. "
          );
        });
    } else {
      axios
        .post(
          `${process.env.REACT_APP_API_REPORT}${report.url}`,
          {
            ...body,
          },

          {
            headers: {
              Authorization:
                "Bearer " + JSON.parse(localStorage.getItem("currUserToken")),
            },
            responseType: "blob",
          }
        )
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", this.generateFileName(report, body));
          document.body.appendChild(link);
          link.click();
        })
        .then(() => this.setState({ fetchingFileName: null }))
        .catch((err) => {
          console.error("Download failed with error:", err);
          console.log(err);
          console.log("Download failed with error:", err);
          this.setState({ fetchingFileName: null });
          alert(
            "Something went wrong ! Please contact your administrator for details. "
          );
        });
    }
  };

  downloadFromUrl = async (report) => {
    this.setState({ fetchingFileName: report.key });

    try {
      const response = await AxiosClient.get(
        `${process.env.REACT_APP_API_URL}${report.url}`,
        {
          headers: {
            Authorization:
              "Bearer " + JSON.parse(localStorage.getItem("currUserToken")),
          },
        }
      );

      const { download_url } = response?.data;

      if (!download_url) {
        console.log("Download URL not found in response");
        throw new Error("Download URL not found in response");
      }

      const fileResponse = await axios.get(download_url, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([fileResponse.data]));
      const link = document.createElement("a");
      link.href = url;
      const fileName = this.generateFileName(report, null);
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Direct download failed:", error);
    } finally {
      this.setState({ fetchingFileName: null });
    }
  };

  downloadGeneratableReport = (item, url) => {
    this.setState({ fetchingFileName: item.key });
    axios
      .get(
        `${url}`,

        {
          responseType: "blob",
        }
      )
      .then((res) => {
        const body = this.generatePostBody(item);
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", this.generateFileName(item, body));
        document.body.appendChild(link);
        link.click();
      })
      .then(() => {
        this.setState({
          fetchingFileName: null,
          status: {},
        });
      })
      .catch((err) => {
        console.error("Generatable report download failed with error:", err);
        console.log("Report download failed with error:", err);
        this.setState({ fetchingFileName: null });
        let errorMessage = "No data found";
        if (item.fields?.fields?.date) {
          errorMessage = `${errorMessage} for the selected date`;
        }
        alert(errorMessage);
      });
  };
  render() {
    //this.state.status.status === "Pending"
    const isMerchantPerformanceReport = this.props.report.key === "trn-report";

    const getTimeRemaining = () => {
      if (!this.state.generationTime) return null;
      const now = new Date();
      const elapsed = now - this.state.generationTime;
      const remaining = AUTO_DOWNLOAD_TIMEOUT_MS - elapsed;
      return Math.max(0, Math.floor(remaining / 60000));
    };

    const timeRemaining = getTimeRemaining();

    return (
      <div
        style={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <button
          className="download-report"
          onClick={() => {
            if (isMerchantPerformanceReport && this.state.reportReady) {
              this.performDelayedDownload();
            } else if (isMerchantPerformanceReport) {
              this._alternateDownload(this.props.report);
            } else {
              this._initiateDownload(this.props.report);
            }
          }}
          disabled={this.props.isDisabled}
          style={{
            minWidth: "140px",
            width: "auto",
          }}
        >
          {this.state.status.status === "Pending" ? (
            <span>
              <CircularProgress
                size={14}
                style={{ color: "rgb(226, 19, 110)" }}
              />
              <p className="generating-report">Generating report</p>
            </span>
          ) : this.state.fetchingFileName ? (
            <CircularProgress
              size={14}
              style={{ color: "rgb(226, 19, 110)" }}
            />
          ) : isMerchantPerformanceReport && this.state.reportReady ? (
            <>
              Click to Download
              <span
                className="generating-report"
                style={{ marginTop: "4px", fontSize: "10px" }}
              >
                Auto-download in {timeRemaining} min.
              </span>
            </>
          ) : (
            "Download"
          )}
        </button>

        {/* {false &&isMerchantPerformanceReport && !this.state.fetchingFileName && (
          <p
            className={
              this.props.isDisabled
                ? "alternate-download disabled-alternate-download"
                : "alternate-download"
            }
            onClick={() => {
              this._alternateDownload(this.props.report);
            }}
          >
            Alternate Download
          </p>
        )} */}
      </div>
    );
  }
}
